import { useMemo } from 'react';
import { Projet, ProjetCalcule, LotCalcule, Lot, MargeATerminaison } from '../types';

export function calculerLot(lot: Lot): LotCalcule {
  const derniereSituation = lot.situations.length > 0
    ? lot.situations[lot.situations.length - 1]
    : null;

  const cumulSituations = derniereSituation ? derniereSituation.montantCumulatif : 0;

  const totalTS = lot.travauxSupplementaires
    .filter(ts => ts.statut === 'valide')
    .reduce((sum, ts) => sum + ts.montant, 0);

  const montantActuel = lot.montantSigne + totalTS;
  const budgetModifie = montantActuel;
  const resteAPayer = montantActuel - cumulSituations;
  const ecart = montantActuel - lot.montantSigne;
  const pourcentageAvancement = montantActuel > 0
    ? Math.round((cumulSituations / montantActuel) * 100)
    : 0;

  // Marge
  const prixDeVente = lot.montantSigne * lot.coefficient;
  const prixDeVenteModifie = budgetModifie * lot.coefficient;
  const margePrevisionnelle = prixDeVente - lot.montantSigne;
  const margeActuelle = prixDeVenteModifie - budgetModifie;
  const tauxMarge = prixDeVenteModifie > 0
    ? (margeActuelle / prixDeVenteModifie) * 100
    : 0;

  // En retard si date fin prévue dépassée et pas terminé
  const now = new Date();
  const finPrevue = new Date(lot.dateFinPrevue);
  const enRetard = lot.statut !== 'termine' && now > finPrevue;

  // ── MAT (Marge À Terminaison) ──
  const avancementReel = lot.avancementPhysiqueReel ?? pourcentageAvancement;
  let coutPrevisionnel: number;
  if (avancementReel >= 5 && avancementReel < 100 && cumulSituations > 0) {
    const coutExtrapole = cumulSituations / (avancementReel / 100);
    // Transition progressive entre 5% et 15% pour éviter les sauts
    if (avancementReel < 15) {
      const ratio = (avancementReel - 5) / 10; // 0 à 5% → 0, à 15% → 1
      coutPrevisionnel = budgetModifie * (1 - ratio) + coutExtrapole * ratio;
    } else {
      coutPrevisionnel = coutExtrapole;
    }
  } else {
    coutPrevisionnel = budgetModifie;
  }
  const prixDeVenteTerminaison = prixDeVenteModifie;
  const margeATerminaison = prixDeVenteTerminaison - coutPrevisionnel;
  const tauxMAT = prixDeVenteTerminaison > 0
    ? (margeATerminaison / prixDeVenteTerminaison) * 100
    : 0;
  const tauxMargeContractuel = (lot.coefficient - 1) / lot.coefficient * 100;
  let tendance: MargeATerminaison['tendance'] = 'stable';
  if (tauxMAT > tauxMargeContractuel + 2) tendance = 'amelioration';
  else if (tauxMAT < tauxMargeContractuel - 2) tendance = 'degradation';

  const mat: MargeATerminaison = {
    coutPrevisionnel,
    prixDeVenteTerminaison,
    margeATerminaison,
    tauxMAT,
    tendance,
  };

  // ── Production (ventilation coûts) ──
  const vc = lot.ventilationCouts;
  const coutDirectTotal = vc
    ? vc.debourseSecMain + vc.debourseSecMateriaux + vc.debourseSecMateriel
    : 0;
  const fraisTotal = vc
    ? vc.fraisGeneraux + vc.fraisEncadrement
    : 0;
  const ratioDebourse = budgetModifie > 0
    ? (coutDirectTotal / budgetModifie) * 100
    : 0;

  // ── Dérive temporelle ──
  let deriveTemporelle = 0;
  if (lot.statut !== 'termine' && lot.statut !== 'non_demarre') {
    const finPrevueMs = new Date(lot.dateFinPrevue).getTime();
    const nowMs = Date.now();
    if (nowMs > finPrevueMs) {
      deriveTemporelle = Math.round((nowMs - finPrevueMs) / (7 * 24 * 60 * 60 * 1000));
    }
  }

  // ── Productivité ──
  let productivite = 0;
  if (lot.dateDebutReelle && lot.statut !== 'non_demarre') {
    const debutReelMs = new Date(lot.dateDebutReelle).getTime();
    const finPrevueMs = new Date(lot.dateFinPrevue).getTime();
    const nowMs = Date.now();
    const dureePrevue = finPrevueMs - debutReelMs;
    const dureeEcoulee = Math.min(nowMs - debutReelMs, dureePrevue);
    if (dureePrevue > 0 && dureeEcoulee > 0) {
      const tempsEcoulePct = (dureeEcoulee / dureePrevue) * 100;
      productivite = tempsEcoulePct > 0
        ? Math.round((avancementReel / tempsEcoulePct) * 100)
        : 0;
    }
  }
  if (lot.statut === 'termine') productivite = 100;

  // ── Sous-traitant ──
  let ecartSousTraitant = 0;
  let tauxAcceptation = 100;
  if (lot.sousTraitant && lot.sousTraitant.situationsSousTraitant.length > 0) {
    ecartSousTraitant = lot.sousTraitant.situationsSousTraitant
      .reduce((s, sit) => s + sit.ecart, 0);
    const totalDemande = lot.sousTraitant.situationsSousTraitant
      .reduce((s, sit) => s + sit.montantDemande, 0);
    const totalAccepte = lot.sousTraitant.situationsSousTraitant
      .reduce((s, sit) => s + sit.montantAccepte, 0);
    tauxAcceptation = totalDemande > 0
      ? Math.round((totalAccepte / totalDemande) * 100)
      : 100;
  }

  return {
    ...lot,
    cumulSituations,
    totalTS,
    montantActuel,
    budgetModifie,
    prixDeVente,
    prixDeVenteModifie,
    margePrevisionnelle,
    margeActuelle,
    tauxMarge,
    resteAPayer,
    ecart,
    pourcentageAvancement,
    enRetard,
    mat,
    coutDirectTotal,
    fraisTotal,
    ratioDebourse,
    deriveTemporelle,
    productivite,
    ecartSousTraitant,
    tauxAcceptation,
  };
}

export function calculerProjet(projet: Projet): ProjetCalcule {
  const lotsCalcules = projet.lots.map(calculerLot);

  const budgetSigne = lotsCalcules.reduce((s, l) => s + l.montantSigne, 0);
  const budgetActuel = lotsCalcules.reduce((s, l) => s + l.montantActuel, 0);
  const cumulTotal = lotsCalcules.reduce((s, l) => s + l.cumulSituations, 0);

  // Marge agrégée
  const prixDeVenteTotal = lotsCalcules.reduce((s, l) => s + l.prixDeVente, 0);
  const prixDeVenteModifieTotal = lotsCalcules.reduce((s, l) => s + l.prixDeVenteModifie, 0);
  const margeGlobale = prixDeVenteModifieTotal - budgetActuel;
  const tauxMargeGlobal = prixDeVenteModifieTotal > 0
    ? (margeGlobale / prixDeVenteModifieTotal) * 100
    : 0;

  const pourcentageFinancier = budgetActuel > 0
    ? Math.round((cumulTotal / budgetActuel) * 100)
    : 0;

  // Pourcentage temporel
  const debut = new Date(projet.dateDebut).getTime();
  const fin = new Date(projet.dateFin).getTime();
  const now = Date.now();
  const dureeTotal = fin - debut;
  const dureeEcoulee = Math.max(0, Math.min(now - debut, dureeTotal));
  const pourcentageTemporel = dureeTotal > 0
    ? Math.round((dureeEcoulee / dureeTotal) * 100)
    : 0;

  // Alertes
  const alertes: string[] = [];
  if (pourcentageFinancier > pourcentageTemporel + 15) {
    alertes.push('Avancement financier en avance sur le calendrier');
  }
  if (pourcentageTemporel > pourcentageFinancier + 20) {
    alertes.push('Retard financier par rapport au calendrier');
  }
  if (budgetActuel > budgetSigne * 1.05) {
    alertes.push(`Dépassement budget : +${Math.round(((budgetActuel - budgetSigne) / budgetSigne) * 100)}%`);
  }
  const lotsEnRetard = lotsCalcules.filter(l => l.enRetard);
  if (lotsEnRetard.length > 0) {
    alertes.push(`${lotsEnRetard.length} lot(s) en retard`);
  }

  // ── MAT agrégé projet ──
  const coutPrevisionnelTotal = lotsCalcules.reduce((s, l) => s + l.mat.coutPrevisionnel, 0);
  const pvTerminaisonTotal = lotsCalcules.reduce((s, l) => s + l.mat.prixDeVenteTerminaison, 0);
  const matProjet = pvTerminaisonTotal - coutPrevisionnelTotal;
  const tauxMATProjet = pvTerminaisonTotal > 0
    ? (matProjet / pvTerminaisonTotal) * 100
    : 0;
  const coeffMoyen = budgetSigne > 0
    ? prixDeVenteTotal / budgetSigne
    : 1.2;
  const tauxMargeContractuelProjet = (coeffMoyen - 1) / coeffMoyen * 100;
  let tendanceProjet: MargeATerminaison['tendance'] = 'stable';
  if (tauxMATProjet > tauxMargeContractuelProjet + 2) tendanceProjet = 'amelioration';
  else if (tauxMATProjet < tauxMargeContractuelProjet - 2) tendanceProjet = 'degradation';

  const mat: MargeATerminaison = {
    coutPrevisionnel: coutPrevisionnelTotal,
    prixDeVenteTerminaison: pvTerminaisonTotal,
    margeATerminaison: matProjet,
    tauxMAT: tauxMATProjet,
    tendance: tendanceProjet,
  };

  // ── Production agrégée ──
  const coutDirectTotalProjet = lotsCalcules.reduce((s, l) => s + l.coutDirectTotal, 0);
  const fraisTotalProjet = lotsCalcules.reduce((s, l) => s + l.fraisTotal, 0);

  // ── Dérive temporelle moyenne ──
  const lotsAvecDerive = lotsCalcules.filter(l => l.deriveTemporelle > 0);
  const deriveTemporelleMoyenne = lotsAvecDerive.length > 0
    ? Math.round(lotsAvecDerive.reduce((s, l) => s + l.deriveTemporelle, 0) / lotsAvecDerive.length)
    : 0;

  // ── Sous-traitant agrégé ──
  const lotsST = lotsCalcules.filter(l => l.sousTraitant);
  const ecartSousTraitantTotal = lotsCalcules.reduce((s, l) => s + l.ecartSousTraitant, 0);
  const nbLotsSousTraites = lotsST.length;
  const nbLotsProblemesST = lotsST.filter(l =>
    l.sousTraitant!.noteEvaluation != null && l.sousTraitant!.noteEvaluation <= 2
  ).length;

  // ── Incidents ──
  const incidentsOuverts = (projet.incidents || []).filter(
    i => i.statut === 'ouvert' || i.statut === 'en_cours'
  );
  const incidentsResolus = (projet.incidents || []).filter(
    i => i.statut === 'resolu'
  );
  const impactFinancierIncidents = incidentsOuverts.reduce(
    (s, i) => s + (i.impactFinancierEstime || 0), 0
  );

  return {
    ...projet,
    budgetSigne,
    budgetActuel,
    cumulTotal,
    prixDeVenteTotal,
    prixDeVenteModifieTotal,
    margeGlobale,
    tauxMargeGlobal,
    pourcentageFinancier,
    pourcentageTemporel,
    alertes,
    lotsCalcules,
    mat,
    coutDirectTotalProjet,
    fraisTotalProjet,
    deriveTemporelleMoyenne,
    ecartSousTraitantTotal,
    nbLotsSousTraites,
    nbLotsProblemesST,
    incidentsOuverts,
    incidentsResolus,
    impactFinancierIncidents,
  };
}

export function useProjetCalculations(projet: Projet): ProjetCalcule {
  return useMemo(() => calculerProjet(projet), [projet]);
}
